import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FileSpreadsheet, Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import ExcelImportModal from '../../components/ExcelImportModal'
import DataTable from '../../components/DataTable'
import IconButton from '../../components/IconButton'
import PageHeader from '../../components/PageHeader'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import QuestionFormDrawer from './QuestionFormDrawer'
import {
  deleteQuestion,
  getQuestion,
  getQuestionsPage,
  importQuestions,
  listQuestions,
  saveQuestion,
} from './contentService'
import { questionsStrings as S } from './strings'
import './content.css'
import { serverText } from '../../lib/i18n'

/**
 * `answer` is the 1-based index of the accepted option.
 *
 * The row carries `options` as an ARRAY — `[Option1, Option2, Option3, Option4]`. Reading
 * `row.option1`…`option4` looked for properties the mapper never produces, so the
 * "الإجابة المقبولة" column was empty on every question in the bank.
 */
const acceptedOption = (row) => row.options?.[Number(row.answer) - 1] ?? ''

/** قائمة الأسئلة — Views/EditQuestions/Index.cshtml. */
export default function QuestionsPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const toast = useToast()
  const [pendingDelete, setPendingDelete] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [importing, setImporting] = useState(false)

  /**
   * The list row is ONE language — `QuestionItemDto` is localized server-side — and the
   * form edits both, so an edit fetches the question rather than reusing the row.
   */
  const openForm = useCallback(async (question) => {
    if (!question) {
      setEditing(null)
      setFormOpen(true)
      return
    }

    const full = await getQuestion(question.id).catch(() => null)
    if (!full) return toast.error(S.deleteFailed)

    setEditing(full)
    setFormOpen(true)
  }, [toast])

  /* The topic and difficulty options come from the page payload, VALUED BY ID.
     They used to be derived from the rows' own localized names and valued by the name —
     which reads the same on screen but sends a word where `SaveQuestion` parses an int,
     so every save landed in category 0. */
  const [lookups, setLookups] = useState({ categories: [], difficulties: [] })
  const [reloadKey, setReloadKey] = useState(0)

  /* THE TOPIC FILTER. `listQuestions` has always accepted `categoryId` and nothing ever
     passed one, so the screen listed the whole bank however it was reached — including from
     `/admin/edit-questions?categoryId=43`, whose parameter the rewrite preserves and this
     page ignored. The URL seeds it so that link lands where it says it will. */
  const [params, setParams] = useSearchParams()
  const categoryId = params.get('categoryId') ?? ''

  useEffect(() => {
    getQuestionsPage()
      .then((page) => setLookups({ categories: page.categories, difficulties: page.difficulties }))
      .catch(() => setLookups({ categories: [], difficulties: [] }))
  }, [])

  useEffect(() => {
    setLoading(true)
    listQuestions({ categoryId: categoryId || null })
      .then((response) => {
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.deleteFailed)
      })
  }, [reloadKey, toast, categoryId])

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="content__index">{index + 1}</span>,
      },
      {
        key: 'text',
        header: S.columns.text,
        render: (row) => (
          <bdi className="content__question" title={row.text}>
            {row.text}
          </bdi>
        ),
      },
      {
        key: 'answer',
        header: S.columns.answer,
        value: acceptedOption,
        render: (row) => (
          <bdi className="content__answer" title={acceptedOption(row)}>
            {acceptedOption(row)}
          </bdi>
        ),
      },
      {
        key: 'category',
        header: S.columns.category,
        render: (row) => <bdi>{row.category}</bdi>,
      },
      {
        key: 'difficulty',
        header: S.columns.difficulty,
        render: (row) => <StatusBadge tone="primary" plain>{row.difficulty}</StatusBadge>,
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="data-table__row-actions">
            <IconButton icon={Pencil} label={S.row.edit} onClick={() => openForm(row)} />
            <IconButton
              icon={Trash2}
              label={S.row.delete}
              tone="danger"
              onClick={() => setPendingDelete(row)}
            />
          </span>
        ),
      },
    ],
    [openForm],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : rows.length}
        actions={
          <>
            <Button onClick={() => openForm(null)}>
              <Plus size={16} strokeWidth={2} />
              {S.actions.create}
            </Button>
            <Button variant="soft" onClick={() => setImporting(true)}>
              <FileSpreadsheet size={16} strokeWidth={1.75} />
              {S.actions.excel}
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row) => row.id}
        initialSort={{ key: 'text', dir: 'asc' }}
        toolbar={
          /* The bank is long and every question belongs to a topic, so narrowing by one is
             the first thing anyone wants. It writes the choice to the URL, which is also
             what makes `?categoryId=` — the parameter the legacy link carries — a real
             destination rather than something the screen quietly ignores. */
          <Select
            value={categoryId}
            onChange={(value) => {
              const next = new URLSearchParams(params)
              if (value) next.set('categoryId', value)
              else next.delete('categoryId')
              setParams(next, { replace: true })
            }}
            placeholder={S.columns.category}
            options={[
              { value: '', label: S.filter?.allTopics ?? S.columns.category },
              ...lookups.categories,
            ]}
          />
        }
      />

      {/* The legacy sheet is a template link and a file box — no column table,
          which is why this one passes no `columns`. */}
      <ExcelImportModal
        open={importing}
        title={S.actions.excel}
        templateHref="/root/ExcelTemplates/Questions.xlsx"
        onClose={() => setImporting(false)}
        // The modal hands over the FILE itself — see the note on CategoriesPage.
        onSubmit={async (file) => {
          const outcome = await importQuestions({ file })
          if (!outcome.ok) return toast.error(outcome.message || S.deleteFailed)
          setImporting(false)
          toast.success(serverText(outcome.message) || S.imported)
          setReloadKey((key) => key + 1)
        }}
      />

      <QuestionFormDrawer
        open={formOpen}
        question={editing}
        categories={lookups.categories}
        levels={lookups.difficulties}
        onClose={() => setFormOpen(false)}
        /* This closed and toasted "deleted" — the wrong word for a save, and nothing was
           written either way. */
        onSave={async (values) => {
          const outcome = await saveQuestion({ ...values, id: editing?.id })
          if (!outcome.ok) return toast.error(outcome.message || S.deleteFailed)
          setFormOpen(false)
          toast.success(serverText(outcome.message) || S.saved)
          setReloadKey((key) => key + 1)
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await deleteQuestion(pendingDelete.id)
          if (!outcome.ok) return toast.error(outcome.message || S.deleteFailed)
          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
